<template>
  <SuiteSuccessfulCase :suite-data="contextData" />
</template>

<script setup lang="ts">
  import type { ISuccessfulCase } from '@/components/Shared/Suite/SuccessfulCase/index.vue';

  const { t, locale } = useI18n({ useScope: 'local' });
  const { blobDomain } = useWebsite();
  const { provideResponsiveImgName } = useImagePath();

  const contextData = computed(
    (): ISuccessfulCase => ({
      component: 'successful-case',
      meta: {
        navigable: '',
      },
      data: {
        sectionName: t('section-name'),
        title: t('title'),
        tabs: [
          {
            tabName: t('item-1-tab-name'),
            name: t('item-1-title'),
            description: t('item-1-description'),
            customerLogo: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('logo_mi-rai')}`,
            customerName: t('item-1-customer-name'),
            region: t('item-1-region'),
            link: `/${locale.value}/marketplace/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('solar-pv-monitoring')}`,
          },
          {
            tabName: t('item-2-tab-name'),
            name: t('item-2-title'),
            description: t('item-2-description'),
            customerLogo: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('logo_yageo')}`,
            customerName: t('item-2-customer-name'),
            region: t('item-2-region'),
            link: `/${locale.value}/marketplace/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('electronics-manufacturing')}`,
          },
          {
            tabName: t('item-3-tab-name'),
            name: t('item-3-title'),
            description: t('item-3-description'),
            customerLogo: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('logo_tct')}`,
            customerName: t('item-3-customer-name'),
            region: t('item-3-region'),
            link: `/${locale.value}/marketplace/materials/tct-integral-wise-iot-predictive-maintenance`,
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('industrial-oem')}`,
          },
        ],
      },
    })
  );
</script>

<i18n lang="json">
{
  "en-int": {
    "section-name": "Use Cases",
    "title": "Explore Digital O&M Use Cases",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-title": "Boosting Solar O&M Efficiency",
    "item-1-description": "Centralized monitoring of <span class=\"font-bold text-adv_blue-400\">400+ solar sites with 90% faster onboarding.</span> The WISE platform delivers 24/7 visibility and faster anomaly response for distributed solar operations.",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "Singapore",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-title": "Driving Energy-Efficient Manufacturing",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">Improved equipment utilization by 10% and increased yield from 78% to 88.5%.</span> WISE provides 24/7 monitoring of energy and equipment performance, enabling data-driven decisions and more sustainable manufacturing.",
    "item-2-customer-name": "YAGEO Corporation",
    "item-2-region": "Taiwan",
    "item-3-tab-name": "Industrial OEM",
    "item-3-title": "Empowering Predictive Maintenance",
    "item-3-description": "WISE PHM empowers manufacturers with predictive maintenance, enabling <span class=\"font-bold text-adv_blue-400\">7-day early failure prediction</span> to reduce downtime, improve equipment reliability, and support more proactive maintenance across production operations.",
    "item-3-customer-name": "TCT",
    "item-3-region": "France"
  },
  "en-jp": {
    "section-name": "Use Cases",
    "title": "Explore Digital O&M Use Cases",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-title": "Boosting Solar O&M Efficiency",
    "item-1-description": "Centralized monitoring of <span class=\"font-bold text-adv_blue-400\">400+ solar sites with 90% faster onboarding.</span> The WISE platform delivers 24/7 visibility and faster anomaly response for distributed solar operations.",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "Singapore",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-title": "Driving Energy-Efficient Manufacturing",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">Improved equipment utilization by 10% and increased yield from 78% to 88.5%.</span> WISE provides 24/7 monitoring of energy and equipment performance, enabling data-driven decisions and more sustainable manufacturing.",
    "item-2-customer-name": "YAGEO Corporation",
    "item-2-region": "Taiwan",
    "item-3-tab-name": "Industrial OEM",
    "item-3-title": "Empowering Predictive Maintenance",
    "item-3-description": "WISE PHM empowers manufacturers with predictive maintenance, enabling <span class=\"font-bold text-adv_blue-400\">7-day early failure prediction</span> to reduce downtime, improve equipment reliability, and support more proactive maintenance across production operations.",
    "item-3-customer-name": "TCT",
    "item-3-region": "France"
  },
  "en-tw": {
    "section-name": "Use Cases",
    "title": "Explore Digital O&M Use Cases",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-title": "Boosting Solar O&M Efficiency",
    "item-1-description": "Centralized monitoring of <span class=\"font-bold text-adv_blue-400\">400+ solar sites with 90% faster onboarding.</span> The WISE platform delivers 24/7 visibility and faster anomaly response for distributed solar operations.",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "Singapore",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-title": "Driving Energy-Efficient Manufacturing",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">Improved equipment utilization by 10% and increased yield from 78% to 88.5%.</span> WISE provides 24/7 monitoring of energy and equipment performance, enabling data-driven decisions and more sustainable manufacturing.",
    "item-2-customer-name": "YAGEO Corporation",
    "item-2-region": "Taiwan",
    "item-3-tab-name": "Industrial OEM",
    "item-3-title": "Empowering Predictive Maintenance",
    "item-3-description": "WISE PHM empowers manufacturers with predictive maintenance, enabling <span class=\"font-bold text-adv_blue-400\">7-day early failure prediction</span> to reduce downtime, improve equipment reliability, and support more proactive maintenance across production operations.",
    "item-3-customer-name": "TCT",
    "item-3-region": "France"
  },
  "en-kr": {
    "section-name": "Use Cases",
    "title": "Explore Digital O&M Use Cases",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-title": "Boosting Solar O&M Efficiency",
    "item-1-description": "Centralized monitoring of <span class=\"font-bold text-adv_blue-400\">400+ solar sites with 90% faster onboarding.</span> The WISE platform delivers 24/7 visibility and faster anomaly response for distributed solar operations.",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "Singapore",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-title": "Driving Energy-Efficient Manufacturing",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">Improved equipment utilization by 10% and increased yield from 78% to 88.5%.</span> WISE provides 24/7 monitoring of energy and equipment performance, enabling data-driven decisions and more sustainable manufacturing.",
    "item-2-customer-name": "YAGEO Corporation",
    "item-2-region": "Taiwan",
    "item-3-tab-name": "Industrial OEM",
    "item-3-title": "Empowering Predictive Maintenance",
    "item-3-description": "WISE PHM empowers manufacturers with predictive maintenance, enabling <span class=\"font-bold text-adv_blue-400\">7-day early failure prediction</span> to reduce downtime, improve equipment reliability, and support more proactive maintenance across production operations.",
    "item-3-customer-name": "TCT",
    "item-3-region": "France"
  },
  "en-americas": {
    "section-name": "Use Cases",
    "title": "Explore Digital O&M Use Cases",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-title": "Boosting Solar O&M Efficiency",
    "item-1-description": "Centralized monitoring of <span class=\"font-bold text-adv_blue-400\">400+ solar sites with 90% faster onboarding.</span> The WISE platform delivers 24/7 visibility and faster anomaly response for distributed solar operations.",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "Singapore",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-title": "Driving Energy-Efficient Manufacturing",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">Improved equipment utilization by 10% and increased yield from 78% to 88.5%.</span> WISE provides 24/7 monitoring of energy and equipment performance, enabling data-driven decisions and more sustainable manufacturing.",
    "item-2-customer-name": "YAGEO Corporation",
    "item-2-region": "Taiwan",
    "item-3-tab-name": "Industrial OEM",
    "item-3-title": "Empowering Predictive Maintenance",
    "item-3-description": "WISE PHM empowers manufacturers with predictive maintenance, enabling <span class=\"font-bold text-adv_blue-400\">7-day early failure prediction</span> to reduce downtime, improve equipment reliability, and support more proactive maintenance across production operations.",
    "item-3-customer-name": "TCT",
    "item-3-region": "France"
  },
  "en-asia-pacific": {
    "section-name": "Use Cases",
    "title": "Explore Digital O&M Use Cases",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-title": "Boosting Solar O&M Efficiency",
    "item-1-description": "Centralized monitoring of <span class=\"font-bold text-adv_blue-400\">400+ solar sites with 90% faster onboarding.</span> The WISE platform delivers 24/7 visibility and faster anomaly response for distributed solar operations.",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "Singapore",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-title": "Driving Energy-Efficient Manufacturing",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">Improved equipment utilization by 10% and increased yield from 78% to 88.5%.</span> WISE provides 24/7 monitoring of energy and equipment performance, enabling data-driven decisions and more sustainable manufacturing.",
    "item-2-customer-name": "YAGEO Corporation",
    "item-2-region": "Taiwan",
    "item-3-tab-name": "Industrial OEM",
    "item-3-title": "Empowering Predictive Maintenance",
    "item-3-description": "WISE PHM empowers manufacturers with predictive maintenance, enabling <span class=\"font-bold text-adv_blue-400\">7-day early failure prediction</span> to reduce downtime, improve equipment reliability, and support more proactive maintenance across production operations.",
    "item-3-customer-name": "TCT",
    "item-3-region": "France"
  },
  "en-emea": {
    "section-name": "Use Cases",
    "title": "Explore Digital O&M Use Cases",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-title": "Boosting Solar O&M Efficiency",
    "item-1-description": "Centralized monitoring of <span class=\"font-bold text-adv_blue-400\">400+ solar sites with 90% faster onboarding.</span> The WISE platform delivers 24/7 visibility and faster anomaly response for distributed solar operations.",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "Singapore",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-title": "Driving Energy-Efficient Manufacturing",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">Improved equipment utilization by 10% and increased yield from 78% to 88.5%.</span> WISE provides 24/7 monitoring of energy and equipment performance, enabling data-driven decisions and more sustainable manufacturing.",
    "item-2-customer-name": "YAGEO Corporation",
    "item-2-region": "Taiwan",
    "item-3-tab-name": "Industrial OEM",
    "item-3-title": "Empowering Predictive Maintenance",
    "item-3-description": "WISE PHM empowers manufacturers with predictive maintenance, enabling <span class=\"font-bold text-adv_blue-400\">7-day early failure prediction</span> to reduce downtime, improve equipment reliability, and support more proactive maintenance across production operations.",
    "item-3-customer-name": "TCT",
    "item-3-region": "France"
  },
  "ja-jp": {
    "section-name": "ユースケース",
    "title": "デジタルO&Mのユースケースを探る",
    "item-1-tab-name": "太陽光発電モニタリング",
    "item-1-title": "太陽光O&Mの運用効率を向上",
    "item-1-description": "<span class=\"font-bold text-adv_blue-400\">400拠点以上</span>の太陽光発電を集中監視し、<span class=\"font-bold text-adv_blue-400\">導入時間を90％短縮</span>、迅速な異常対応を実現します。",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "シンガポール",
    "item-2-tab-name": "電子製造",
    "item-2-title": "高効率なエネルギー管理を実現",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">設備稼働率を10％向上し、歩留まりを78％から88.5％へ改善。</span><br/>設備とエネルギーの可視化により、持続可能な製造を支援します。",
    "item-2-customer-name": "YAGEO Corporation",
    "item-2-region": "台湾",
    "item-3-tab-name": "産業OEM",
    "item-3-title": "予知保全による運用高度化",
    "item-3-description": "<span class=\"font-bold text-adv_blue-400\">設備故障を7日前に予測し</span>、ダウンタイムを削減。<br/>設備信頼性の向上と保全の最適化を実現します。",
    "item-3-customer-name": "TCT",
    "item-3-region": "フランス"
  },
  "zh-cn": {
    "section-name": "Use Cases",
    "title": "Explore Digital O&M Use Cases",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-title": "Boosting Solar O&M Efficiency",
    "item-1-description": "Centralized monitoring of <span class=\"font-bold text-adv_blue-400\">400+ solar sites with 90% faster onboarding.</span> The WISE platform delivers 24/7 visibility and faster anomaly response for distributed solar operations.",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "Singapore",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-title": "Driving Energy-Efficient Manufacturing",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">Improved equipment utilization by 10% and increased yield from 78% to 88.5%.</span> WISE provides 24/7 monitoring of energy and equipment performance, enabling data-driven decisions and more sustainable manufacturing.",
    "item-2-customer-name": "YAGEO Corporation",
    "item-2-region": "Taiwan",
    "item-3-tab-name": "Industrial OEM",
    "item-3-title": "Empowering Predictive Maintenance",
    "item-3-description": "WISE PHM empowers manufacturers with predictive maintenance, enabling <span class=\"font-bold text-adv_blue-400\">7-day early failure prediction</span> to reduce downtime, improve equipment reliability, and support more proactive maintenance across production operations.",
    "item-3-customer-name": "TCT",
    "item-3-region": "France"
  },
  "zh-tw": {
    "section-name": "應用案例",
    "title": "探索數位維運的實際應用",
    "item-1-tab-name": "太陽光電監控",
    "item-1-title": "提升太陽能維運效率",
    "item-1-description": "集中監管逾 <span class=\"font-bold text-adv_blue-400\">400 座太陽能電站，縮短導入時程達 90%</span>。為分散式太陽能維運提供全天候可視性，並大幅提升異常事件的應變速度。",
    "item-1-customer-name": "Mirai Electronics",
    "item-1-region": "新加坡",
    "item-2-tab-name": "電子製造",
    "item-2-title": "實現高能效製造",
    "item-2-description": "<span class=\"font-bold text-adv_blue-400\">設備利用率提升 10%，良率由 78% 提升至 88.5%。</span>提供 24 /7 不間斷的設備與能源監管，支持數據驅動決策，推動更高效且永續製造。",
    "item-2-customer-name": "國巨",
    "item-2-region": "台灣",
    "item-3-tab-name": "工業設備製造（OEM）",
    "item-3-title": "強化預測維護能力",
    "item-3-description": "WISE PHM 賦能製造商預測性維護能力，可提前 <span class=\"font-bold text-adv_blue-400\">7 天預測設備故障</span>，有效降低停機風險，提升設備可靠性，並在整個生產流程中實現更主動積極的維護管理。",
    "item-3-customer-name": "TCT",
    "item-3-region": "法國"
  }
}
</i18n>
